import { AI_CHAT_PATHNAME } from "@/constants/pathname";
import { Text } from "../ui/text";
import MessageBox from "@/components/message/message-box";
import { useDemoChatStreamMutation } from "@/hooks/services/mutations/ai-agent/use-demo-chat-stream-mutation";
import { useAiAgentStore } from "@/stores/ai-agent";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AiAgentNotEnoughCredit from "@/components/ai/ai-agent-not-enough-credit";
const AiHomeContainer = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const getChatSourceSettings = useAiAgentStore((state) => state.getChatSourceSettings);
  const selectedJob = useAiAgentStore((state) => state.selectedJob);
  const clearSelectedJob = useAiAgentStore((state) => state.clearSelectedJob);
  const showCreditError = useAiAgentStore((state) => state.showCreditError);
  const setShowCreditError = useAiAgentStore((state) => state.setShowCreditError);
  const { mutate: sendStreamMessage } = useDemoChatStreamMutation();

  // 페이지 이동 시 selectedJob 초기화
  useEffect(() => {
    return () => {
      clearSelectedJob();
    };
  }, [clearSelectedJob]);

  // Credit 에러 Toast 표시
  useEffect(() => {
    if (showCreditError) {
      toast.custom(
        () => <AiAgentNotEnoughCredit />,
        {
          position: "bottom-center",
          duration: 5000,
        }
      );
      setShowCreditError(false); // 표시 후 리셋
    }
  }, [showCreditError, setShowCreditError]);

  const handleSend = useCallback(
    (text: string, files?: File[]) => {
      const settings = getChatSourceSettings("temp");

      // 임시 아이디 생성: temp-{timestamp}-{난수} 형식(고유 ID를 위해)
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // "temp" 키의 설정을 tempId 키로 한 번에 복사 (단일 상태 업데이트)
      // → 채팅 페이지에서 SourceSelectPopover가 tempId로 조회할 때 설정이 보이도록
      // → 이후 migrateChatSourceSettings(tempId, realChatId)가 정상 동작하도록 보장
      const tempEntry = useAiAgentStore.getState().chatSourceSettingsMap.get("temp");
      if (tempEntry) {
        useAiAgentStore.setState((state) => {
          const newMap = new Map(state.chatSourceSettingsMap);
          newMap.set(tempId, { ...tempEntry });
          return { chatSourceSettingsMap: newMap };
        });
      }

      sendStreamMessage({
        chatId: tempId, //임시 채팅방
        newMessage: text,
        deepResearch: settings.deepResearch,
        source: {
          web_search_enabled: settings.webSearchEnabled,
          private_kb_enabled: settings.privateKbEnabled,
          biomcp_enabled: settings.biomcpEnabled,
          paper_search_preferred: settings.academicJournalEnabled,
        },
        files,
        selectedJob: selectedJob?.jobPlanDTO.id,
        sdfJobName: selectedJob?.analysisNo,
      });
    },
    [navigate, sendStreamMessage, getChatSourceSettings, selectedJob]
  );

  return (
    <main className="flex-1 flex flex-col items-center justify-center relative overflow-y-auto px-4 lg:px-0">
      <div className="flex flex-col gap-[28px] w-full max-w-[724px]">
        <Text variant={"headline-lg"} className="text-center">
          How can I help you?
        </Text>
        <MessageBox onSend={handleSend} />
      </div>
      <div className="flex justify-center items-center absolute bottom-[16px] left-[50%] translate-x-[-50%]">
        <Text variant={"body-md"} className="text-grey-60">
           By sending a message to CellKey AI, you agree to our{" "}
          <span className="underline">Terms of Use</span> &{" "}
          <span className="underline">Privacy Policy</span>
        </Text>
      </div>
    </main>
  );
};

export default AiHomeContainer;
